import moment from 'moment'

// The order time a store is checked against:
//   null                              -> not known yet (slots still loading), treated like ASAP
//   { isAsap: true }                  -> now
//   { isAsap: false, date, minutes }  -> date 'YYYY-MM-DD' and minutes from midnight, store local time

const toMinutes = (hhmm) => {
  const [hours, minutes] = String(hhmm).split(':').map(Number)
  return hours * 60 + minutes
}

const findService = (store, serviceTypeId) =>
  (store?.services || []).find((service) => service.service_type_id == serviceTypeId) || null

// `service_schedule` (from GET /stores?service_schedule=true) holds the service's ranges per date in
// minutes; a range can run past midnight (end > 1440), so the previous day is checked too.
const scheduleCovers = (schedule, date, minutes) => {
  const inRanges = (ranges, value) => (ranges || []).some(([start, end]) => value >= start && value < end)
  const previousDay = moment(date, 'YYYY-MM-DD').subtract(1, 'day').format('YYYY-MM-DD')
  return inRanges(schedule[date], minutes) || inRanges(schedule[previousDay], minutes + 1440)
}

// Weekly business hours, for stores without a service schedule in the response.
const hoursCover = (hours, date, minutes) => {
  const dow = moment(date, 'YYYY-MM-DD').isoWeekday()
  return hours
    .filter((hour) => hour.iso_dow === dow && hour.start_time && hour.end_time)
    .some((hour) => {
      const start = toMinutes(hour.start_time)
      let end = toMinutes(hour.end_time)
      if (end <= start) end += 1440
      return minutes >= start && minutes < end
    })
}

const isStoreToday = (store, date) => {
  const today = store?.today ? moment(store.today, 'MM/DD/YYYY') : moment()
  return today.format('YYYY-MM-DD') === date
}

// Can the store take this service at the order time? ASAP means open right now; a future time has
// to fall inside the store's hours for that day.
export const isStoreAvailableAt = (store, serviceTypeId, orderTime) => {
  const service = findService(store, serviceTypeId)
  if (!orderTime || orderTime.isAsap) {
    return !!store?.is_open && !service?.offline_today
  }
  if (service?.offline_today && isStoreToday(store, orderTime.date)) return false
  // A date the schedule doesn't list at all is unknown, not closed (the list call may return
  // fewer days than the single-store call); an empty [] for the date means closed that day.
  const schedule = store?.service_schedule?.[serviceTypeId]
  if (schedule && Object.prototype.hasOwnProperty.call(schedule, orderTime.date)) {
    return scheduleCovers(schedule, orderTime.date, orderTime.minutes)
  }
  // nothing to check against: leave it to the day/time slots of the picked store
  if (!Array.isArray(store?.hours)) return true
  return hoursCover(store.hours, orderTime.date, orderTime.minutes)
}

// Business hours shown on a store card for the order's day ('5:30 AM - 2:00 PM'), or null. These are
// the store's opening hours, not the (lead-time trimmed) ordering windows in service_schedule.
export const getStoreHoursForDay = (store, serviceTypeId, orderTime, formatTime) => {
  const date = orderTime && !orderTime.isAsap ? orderTime.date : null
  if (findService(store, serviceTypeId)?.offline_today && (!date || isStoreToday(store, date))) return null
  const dow = (date ? moment(date, 'YYYY-MM-DD') : moment()).isoWeekday()
  const hours = (store?.hours || []).filter((hour) => hour.iso_dow === dow && hour.start_time && hour.end_time)
  if (!hours.length) return null
  const start = hours.map((hour) => hour.start_time).sort()[0]
  const end = hours
    .map((hour) => hour.end_time)
    .sort()
    .pop()
  return `${formatTime(moment(start, 'HH:mm'))} - ${formatTime(moment(end, 'HH:mm'))}`
}
